import { useEffect } from 'react';

// Google Analytics Measurement ID (GA4)
const GA_MEASUREMENT_ID = 'G-5HZFJP49HL'; // Replace with your actual GA4 Measurement ID

// Check if we're in development mode (localhost)
const isDevelopment = () => {
  return typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('localhost')
  );
};

// Initialize Google Analytics
const initializeGA = () => {
  // Skip analytics in development
  if (isDevelopment()) {
    console.log('Analytics disabled in development mode');
    return;
  }

  if (typeof window !== 'undefined' && !window.gtag) {
    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_title: document.title,
      page_location: window.location.href,
    });
  }
};

// Track page view
export const trackPageView = (page: string, title?: string) => {
  // Skip tracking in development
  if (isDevelopment()) {
    console.log('Page view tracking disabled in development:', page);
    return;
  }

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_title: title || page,
      page_location: `${window.location.origin}${page}`,
    });
  }
};

// Track custom events
export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  // Skip tracking in development
  if (isDevelopment()) {
    console.log('Event tracking disabled in development:', { action, category, label, value });
    return;
  }

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track search events
export const trackSearch = (query: string, resultsCount: number) => {
  // Track the actual search query
  trackEvent('search_query', 'address_search', query, resultsCount);
  
  // Also track the search action
  trackEvent('search', 'address_search', `query_${resultsCount}_results`, resultsCount);
};

// Track address selection
export const trackAddressSelect = (addressName: string, district: string) => {
  trackEvent('select_address', 'address_interaction', `${addressName} - ${district}`);
};

// Track voice search
export const trackVoiceSearch = (success: boolean, error?: string) => {
  trackEvent('voice_search', 'search_method', success ? 'success' : 'error', success ? 1 : 0);
  if (error) {
    trackEvent('voice_search_error', 'error', error);
  }
};

// Track speech synthesis
export const trackSpeechSynthesis = (language: string, success: boolean) => {
  trackEvent('speech_synthesis', 'accessibility', language, success ? 1 : 0);
};

// Track theme toggle
export const trackThemeToggle = (theme: string) => {
  trackEvent('theme_toggle', 'preferences', theme);
};

// Track QR code interaction
export const trackQRCodeInteraction = (language: string, action: string) => {
  trackEvent('qr_code', 'navigation', `${language}_${action}`);
};

// Track search query details
export const trackSearchQuery = (query: string, resultsCount: number, hasResults: boolean) => {
  // Track the search query with more context
  trackEvent('search_query_detail', 'address_search', query, resultsCount);
  
  // Track if the search had results or not
  trackEvent('search_result_status', 'address_search', hasResults ? 'has_results' : 'no_results', resultsCount);
  
  // Track query length for analysis
  const queryLength = query.length;
  trackEvent('search_query_length', 'address_search', `length_${queryLength}`, queryLength);
};

// Analytics hook
export const useAnalytics = () => {
  useEffect(() => {
    // Initialize GA on mount
    initializeGA();
    
    // Track initial page view (will be skipped in development)
    trackPageView(window.location.pathname, document.title);
  }, []);

  return {
    trackPageView,
    trackEvent,
    trackSearch,
    trackSearchQuery,
    trackAddressSelect,
    trackVoiceSearch,
    trackSpeechSynthesis,
    trackThemeToggle,
    trackQRCodeInteraction,
  };
};

// TypeScript declarations
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}
